'use client';

import * as React from 'react';
import Image from 'next/image';
import NiceModal, { type NiceModalHocProps } from '@ebay/nice-modal-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@workspace/ui/components/dialog';

import { useEnhancedModal } from '~/hooks/use-enhanced-modal';
import type { PortfolioImageDto } from '~/types/dtos/model-variant-dto';

export type PortfolioImagesModalProps = NiceModalHocProps & {
  modelName: string;
  portfolioImages: PortfolioImageDto[];
};

export const PortfolioImagesModal = NiceModal.create<PortfolioImagesModalProps>(
  ({ modelName, portfolioImages }) => {
    const modal = useEnhancedModal();

    if (portfolioImages.length === 0) {
      return (
        <Dialog
          open={modal.visible}
          onOpenChange={modal.handleOpenChange}
        >
          <DialogContent
            className="max-w-md"
            onClose={modal.handleClose}
            onAnimationEndCapture={modal.handleAnimationEndCapture}
          >
            <DialogHeader>
              <DialogTitle>Portfolio Images</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="text-muted-foreground">
                No portfolio images available for {modelName}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <Dialog
        open={modal.visible}
        onOpenChange={modal.handleOpenChange}
      >
        <DialogContent
          className="max-w-[1500px]"
          onClose={modal.handleClose}
          onAnimationEndCapture={modal.handleAnimationEndCapture}
        >
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-center justify-center">
              <DialogTitle>{modelName} Preview</DialogTitle>
            </div>
          </DialogHeader>

          {/* Horizontal Scrollable Images */}
          <div className="flex-1 overflow-x-auto overflow-y-hidden  snap-x snap-mandatory">
            <div className="flex gap-6 min-w-max justify-center">
              {portfolioImages.map((pi) => (
                <div
                  key={pi.id}
                  className="relative h-[calc(75vh-12rem)] w-auto flex-shrink-0 rounded-lg border bg-muted overflow-hidden snap-center"
                >
                  <Image
                    src={pi.url}
                    alt={`${modelName} portfolio image`}
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="h-full w-auto max-w-none object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);
